import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Col, Label, UncontrolledTooltip } from 'reactstrap'
import { useDebouncedCallback } from 'use-debounce'
import { formatCascadeCompanyOptions, formatCascadeContactOptions } from '../../api/contacts/formatters'
import contacts from '../../api/contacts/hooks'
import { CONTACT_TYPE } from '../../common/constants'
import { activityFields } from '../../common/forms/activities/fields'

// TODO: replace activityFields with just hardcoded field name since component is not only used in ActivityModal
const ContactCompanyCascadeInput = ({ fieldNames, inputValues, formik, selectOptions }) => {
    const { t } = useTranslation()

    const [selectedContact, setSelectedContact] = useState(null)
    const [selectedCompany, setSelectedCompany] = useState(null)
    const [isFirstRun, setIsFirstRun] = useState(true)
    const [mode, setMode] = useState(null)
    const [contactMessage, setContactMessage] = useState('')
    const [defaultContactOptions, setDefaultContactOptions] = useState([])
    const [defaultCompanyOptions, setDefaultCompanyOptions] = useState([])
    const [debouncedContactTerm, setDebouncedContactTerm] = useState('')
    const [debouncedCompanyTerm, setDebouncedCompanyTerm] = useState('')
    const [selectedCompanyIds, setSelectedCompanyIds] = useState([])

    const updateContactTerm = useDebouncedCallback((v) => setDebouncedContactTerm(v), 500)
    const updateCompanyTerm = useDebouncedCallback((v) => setDebouncedCompanyTerm(v), 500)

    useEffect(() => {
        if (
            (inputValues?.[fieldNames[0]] || inputValues?.[fieldNames[1]]?.length) &&
            (selectOptions?.[fieldNames[0]]?.length || selectOptions?.[fieldNames[1]]?.length) &&
            isFirstRun
        ) {
            if (selectOptions[fieldNames[0]]?.length) {
                setDefaultContactOptions(selectOptions[fieldNames[0]])
                setSelectedContact(selectOptions[fieldNames[0]].find((item) => item.value.toString() === inputValues[fieldNames[0]]?.toString()))
            }

            if (selectOptions[fieldNames[1]]?.length && inputValues[fieldNames[1]]?.length) {
                const selectedCompanyOptions = inputValues[fieldNames[1]]
                    .map((item) =>
                        selectOptions[fieldNames[1]].find((option) => option.value.toString() === item[activityFields.CONTACT_ID].toString())
                    )
                    .filter(Boolean)

                setDefaultCompanyOptions(selectOptions[fieldNames[1]])
                setSelectedCompany(selectedCompanyOptions)
            }

            setIsFirstRun(false)
        }
    }, [inputValues, selectOptions, fieldNames, isFirstRun])

    useEffect(() => {
        if (!selectedContact && !selectedCompany) {
            setMode(null)
        }
    }, [selectedContact, selectedCompany])

    // search persons by name as user types in the contact field
    const { data: personResults, isFetching: isContactsFetching } = contacts.GetContacts.useQuery(
        { noLimit: 1, criteria: { contactType: 'person', name: debouncedContactTerm } },
        {
            select: (data) => formatCascadeContactOptions(data.items ?? []),
            enabled: !!debouncedContactTerm && mode !== CONTACT_TYPE.COMPANY,
        }
    )

    // search companies by name as user types in the company field
    const { data: companyResults, isFetching: isCompaniesFetching } = contacts.GetContacts.useQuery(
        { noLimit: 1, criteria: { contactType: 'company', name: debouncedCompanyTerm } },
        {
            select: (data) => formatCascadeCompanyOptions(data.items ?? []),
            enabled: !!debouncedCompanyTerm && mode !== CONTACT_TYPE.PERSON,
        }
    )

    // cascade: when a company is selected, fetch its persons to populate the contact dropdown
    const { data: cascadePersonResults } = contacts.GetContacts.useQuery(
        { noLimit: 1, criteria: { contactType: 'person', contactCompany: selectedCompanyIds } },
        {
            select: (data) => formatCascadeContactOptions(data.items ?? []),
            enabled: selectedCompanyIds.length > 0 && mode === CONTACT_TYPE.COMPANY,
        }
    )

    useEffect(() => {
        if (!cascadePersonResults) return

        const message =
            cascadePersonResults.length > 0
                ? t('contactCompanyCascadeInput.message.selectContactForCompany')
                : t('contactCompanyCascadeInput.message.noContactForCompany')

        setContactMessage(message)
    }, [cascadePersonResults, t])

    // priority: cascade results (company selected) > name search results (user typing) > pre-loaded initial values
    const displayContactOptions = useMemo(() => {
        if (mode === CONTACT_TYPE.COMPANY && selectedCompanyIds.length > 0) return cascadePersonResults ?? []

        if (debouncedContactTerm) return personResults ?? []

        return defaultContactOptions
    }, [mode, selectedCompanyIds, cascadePersonResults, debouncedContactTerm, personResults, defaultContactOptions])

    const displayCompanyOptions = useMemo(() => {
        if (debouncedCompanyTerm) return companyResults ?? []

        return defaultCompanyOptions
    }, [debouncedCompanyTerm, companyResults, defaultCompanyOptions])

    const onContactInputChange = (value) => {
        if (value.length) {
            setMode(CONTACT_TYPE.PERSON)
            updateContactTerm(value)
        }
    }

    const onCompanyInputChange = (value) => {
        if (value.length) {
            setMode(CONTACT_TYPE.COMPANY)
            updateCompanyTerm(value)
        }
    }

    const handleContactSelectChange = (contact) => {
        if (mode !== CONTACT_TYPE.PERSON) {
            setSelectedContact(contact)
            return
        }

        if (!contact) {
            setDefaultCompanyOptions([])
            setSelectedCompany(null)
            setSelectedContact(null)
            return
        }

        setSelectedContact(contact)
        setSelectedCompany([])
        setDefaultCompanyOptions([])

        const contactCompanies = contact.contactPersonCompany
        if (contactCompanies?.length) {
            const companyOptions = contactCompanies.map((item) => ({
                value: item.company.id,
                id: item.company.id,
                label: `ID: ${item.company.internalAppId} - ${item.company.contactCompany.name}`,
                name: item.company.contactCompany.name,
            }))

            const companyFieldValue = companyOptions.map((item) => ({
                [activityFields.CONTACT_ID]: item.value,
                contact_name: item.name ?? '',
            }))

            formik?.setFieldValue(fieldNames[1], companyFieldValue)

            setDefaultCompanyOptions(companyOptions)
            setSelectedCompany(companyOptions)
        }
    }

    const handleCompanySelectChange = (e) => {
        setSelectedCompany(e)

        const companyFieldValue = e?.length ? e.map((item) => ({ [activityFields.CONTACT_ID]: item.value, contact_name: item.name ?? '' })) : []

        formik?.setFieldValue(fieldNames[1], companyFieldValue)

        if (mode !== CONTACT_TYPE.COMPANY) return

        setSelectedContact(null)
        setSelectedCompanyIds(e?.length ? e.map((item) => item.value) : [])
    }

    return (
        <>
            <Col sm={12} className="mb-4">
                <div className="d-flex justify-content-between">
                    <Label for={activityFields.CONTACT_ID} id={`${activityFields.CONTACT_ID}Lbl`}>
                        {t('app.common.contactNameSurname')}
                    </Label>
                    <span id={`${activityFields.CONTACT_ID}Tooltip`}>
                        <i className="mdi mdi-help-circle-outline"></i>{' '}
                    </span>
                </div>
                <Select
                    onInputChange={onContactInputChange}
                    onChange={(e) => {
                        formik?.setFieldValue(fieldNames[0], e ? e.value : '')
                        formik?.setFieldValue(fieldNames[0].replace('contact_id', 'contact_name'), e ? (e.name ?? '') : '')
                        formik?.setFieldValue(fieldNames[0].replace('contact_id', 'contact_email'), e ? (e.email ?? '') : '')
                        handleContactSelectChange(e)
                        setContactMessage('')
                    }}
                    options={displayContactOptions}
                    isClearable={true}
                    name={activityFields.CONTACT_ID}
                    inputId={activityFields.CONTACT_ID}
                    value={selectedContact}
                    noOptionsMessage={() => t('app.common.noOptions')}
                    isLoading={isContactsFetching}
                    loadingMessage={() => t('app.common.loading')}
                    placeholder=""
                />
                <UncontrolledTooltip placement="right" target={`${activityFields.CONTACT_ID}Tooltip`}>
                    {t('app.common.contactNameSurname.tooltip')}
                </UncontrolledTooltip>
                {contactMessage && <div className="text-info mt-1 small">{contactMessage}</div>}
            </Col>
            <Col sm={12} className="mb-4">
                <div className="d-flex justify-content-between">
                    <Label for={activityFields.CONTACT_COMPANY_COLLECTION} id={`${activityFields.CONTACT_COMPANY_COLLECTION}Lbl`}>
                        {t('app.common.company.name')}
                    </Label>
                    <span id={`${activityFields.CONTACT_COMPANY_COLLECTION}Tooltip`}>
                        <i className="mdi mdi-help-circle-outline"></i>{' '}
                    </span>
                </div>
                <Select
                    onInputChange={onCompanyInputChange}
                    onChange={(e) => {
                        handleCompanySelectChange(e)
                        setContactMessage('')
                    }}
                    options={displayCompanyOptions}
                    isMulti={true}
                    isClearable={true}
                    name={activityFields.CONTACT_COMPANY_COLLECTION}
                    id={activityFields.CONTACT_COMPANY_COLLECTION}
                    value={selectedCompany}
                    noOptionsMessage={() => t('app.common.noOptions')}
                    isLoading={isCompaniesFetching}
                    loadingMessage={() => t('app.common.loading')}
                    placeholder=""
                />
                <UncontrolledTooltip placement="right" target={`${activityFields.CONTACT_COMPANY_COLLECTION}Tooltip`}>
                    {t('app.common.company.name.tooltip')}
                </UncontrolledTooltip>
            </Col>
        </>
    )
}

export default ContactCompanyCascadeInput
